/**
 * Helpers for mapping raw reservation documents into the row shape used by
 * the Reservations tables (reservations page, partners and suppliers
 * reservation lists).
 */

/** Resolve the reservation class, defaulting to "Transfers" when not set.
 * @param {string} reservationClass - The reservation's class field.
 * @returns {string} - The resolved reservation class.
 */
export function getReservationClass(reservationClass) {
  return reservationClass ? reservationClass : "Transfers";
}

/** Get a displayable name for a reservation's customer or agent.
 * @param {Object} customer - The customer/agent object.
 * @returns {string} - The formatted name, or "unknown" if not provided.
 */
export function getCustomerName(customer) {
  if (customer) {
    if (["business", "travel_agency"].indexOf(customer.type) > -1) {
      return customer.company_name
        ? customer.company_name
        : `${customer.first_name} ${customer.last_name}`;
    } else {
      return `${customer.first_name} ${customer.last_name}`;
    }
  } else {
    return "unknown";
  }
}

/** Get the payment status label for a reservation row.
 * @param {boolean} payment - Whether the reservation has been paid.
 * @param {string} paymentType - The payment type (e.g. "credit").
 * @param {string} paymentLinkStatus - The payment link status.
 * @param {string} reservationStatus - The reservation status.
 * @returns {string} - "Paid", "On credit" or "Unpaid".
 */
export function getStatusPayment(
  payment,
  paymentType,
  paymentLinkStatus,
  reservationStatus
) {
  if (payment) {
    if (paymentLinkStatus === "refund") {
      return "Paid";
    } else {
      return "Paid";
    }
  } else {
    if (paymentType == "credit") {
      if (reservationStatus !== "PNDG") {
        return "On credit";
      } else {
        return "Unpaid";
      }
    } else {
      return "Unpaid";
    }
  }
}

/** Format a reservation status code into a table-friendly label.
 * @param {string} status - The reservation status code.
 * @returns {string} - The formatted status label.
 */
export function formatStatus(status) {
  if (status === "NW") {
    return "New";
  } else if (status === "SBC" || status === "SCB") {
    return "Cancelled";
  } else if (status === "SC") {
    return "Confirmed";
  } else if (status === "JF") {
    return "Completed";
  } else if (status === "SRB") {
    return "Rejected";
  } else if (status === "DA") {
    return "Driver Accepted";
  } else if (status === "DSR") {
    return "Driver Started Trip";
  } else if (status === "DAP") {
    return "Driver on Location";
  } else if (status === "POB") {
    return "POB";
  } else if (status === "NS") {
    return "No Show";
  } else if (status === "PNDG") {
    return "Pending";
  } else {
    return "Draft";
  }
}

/** Get the supplier's business name for a Transfers reservation.
 * @param {string} reservationClass - "Transfers" or an experience class.
 * @param {Object} supplier - The supplier object.
 * @returns {string|null} - The supplier's business name, or null for non-Transfers.
 */
export function getSupplierName(reservationClass, supplier) {
  if (reservationClass === "Transfers") {
    return supplier.businessDetails.name;
  } else {
    return null;
  }
}

/** Get the payment reference for a Transfers reservation.
 * @param {string} reservationClass - "Transfers" or an experience class.
 * @param {Object} payment - The payment object.
 * @param {string} paymentReference - The fallback payment reference.
 * @returns {string|null} - The resolved payment reference, or null for non-Transfers.
 */
export function getPaymentReference(reservationClass, payment, paymentReference) {
  if (reservationClass === "Transfers") {
    return payment ? payment.transaction_id : paymentReference;
  } else {
    return null;
  }
}

/** Get the driver's full name for a Transfers reservation.
 * @param {string} reservationClass - "Transfers" or an experience class.
 * @param {Object} driver - The driver object.
 * @returns {string|null} - The driver's full name, or null for non-Transfers.
 */
export function getDriverName(reservationClass, driver) {
  if (reservationClass === "Transfers") {
    return driver
      ? `${driver.person.first_name} ${driver.person.last_name}`
      : null;
  } else {
    return null;
  }
}

/** Get the currency symbol for a given currency code.
 * @param {string} currency - The currency code (e.g. "USD").
 * @returns {string} - The currency symbol.
 */
export function getCurrenSymbolByCurrency(currency) {
  if (currency === "USD") {
    return "$";
  } else if (currency === "EUR") {
    return "€";
  } else if (currency === "GBP") {
    return "£";
  }
}

/** Get the currency code/symbol pair for a reservation row.
 * @param {string} reservationClass - "Transfers" or an experience class.
 * @param {Object} service - The Transfers service object (holds currency_code/currency_symbol).
 * @param {Object} pricing - The experience pricing object (holds currency).
 * @returns {{code: string, symbol: string}} - The resolved currency code/symbol.
 */
export function getCurrency(reservationClass, service, pricing) {
  if (reservationClass === "Transfers") {
    return {
      code: service ? service.currency_code : "",
      symbol: service ? service.currency_symbol : ""
    };
  } else {
    return {
      code: pricing.currency,
      symbol: getCurrenSymbolByCurrency(pricing.currency)
    };
  }
}

/** Round a price to 2 decimals.
 * @param {number} price - The raw price.
 * @returns {number} - The price rounded to 2 decimals.
 */
export function getPriceFormat(price) {
  return parseFloat(price.toFixed(2));
}

/** Convert an experience's minor-unit amount into a decimal price string.
 * @param {number} amount - The amount in minor currency units.
 * @param {number} currencyPrecision - The number of decimal places for the currency.
 * @returns {string} - The formatted decimal price.
 */
export function getProductPricing(amount, currencyPrecision) {
  let total = amount / Math.pow(10, currencyPrecision);
  return total.toFixed(2);
}

/** Get the total charge (gross price) for a reservation row. */
export function getTotalCharge(reservationClass, pricing) {
  return reservationClass === "Transfers"
    ? getPriceFormat(pricing.totals.gross_price)
    : getProductPricing(pricing.retail, pricing.currencyPrecision);
}

/** Get the net price for a reservation row. */
export function getNetprice(reservationClass, pricing) {
  return reservationClass === "Transfers"
    ? getPriceFormat(pricing.totals.net_price)
    : getProductPricing(pricing.original, pricing.currencyPrecision);
}

/** Get the quoted (frozen) net price for a reservation row — never changes unless requoted or a service is reselected. */
export function getQuotedPrice(reservationClass, pricing) {
  return reservationClass === "Transfers"
    ? getPriceFormat(pricing.net_price.amount)
    : getProductPricing(pricing.original, pricing.currencyPrecision);
}

/** Get the gross price for a reservation row. */
export function getGrossPrice(reservationClass, pricing) {
  return reservationClass === "Transfers"
    ? getPriceFormat(pricing.totals.gross_price)
    : getProductPricing(pricing.retail, pricing.currencyPrecision);
}

/** Get the net profit for a reservation row (0 for non-Transfers). */
export function getNetProfit(reservationClass, pricing) {
  return reservationClass === "Transfers"
    ? getPriceFormat(pricing.totals.net_profit)
    : 0;
}

/** Get the total extras charge for a reservation row (0 for non-Transfers). */
export function getTotalExtras(reservationClass, pricing) {
  return reservationClass === "Transfers"
    ? getPriceFormat(pricing.totals.extras.gross_price)
    : 0;
}

/** Get the total discounts for a reservation row (0 for non-Transfers). */
export function getTotalDiscounts(reservationClass, pricing) {
  return reservationClass === "Transfers"
    ? getPriceFormat(pricing.totals.discounts)
    : 0;
}

/**
 * Map raw reservation documents into the row shape used by the Reservations
 * table (reservations page, partners and suppliers reservation lists).
 * @param {Array<Object>} reservations - Raw reservation documents (e.g. responseReservations.docs).
 * @returns {Array<Object>} - Table-ready reservation rows.
 */
export function mapReservationsForTable(reservations = []) {
  try {
    return reservations.map(reservation => {
      let reservationClass = getReservationClass(reservation.class);
      return {
        //Commons
        booking_number: reservation.booking_number,
        customer: getCustomerName(
          reservation.agent ? reservation.agent : reservation.customer
        ),
        passengers: reservation.passengers,
        payment: getStatusPayment(
          reservation.payment,
          reservation.payment_type,
          reservation.paymentLink.status,
          reservation.status
        ),
        reservation_reference: reservation.booking_reference,
        deal_number: reservation.deal_number,
        invoice_n: reservation.invoice_number,
        payment_reference: getPaymentReference(
          reservationClass,
          reservation.payment,
          reservation.payment_reference
        ),
        status: formatStatus(reservation.status),

        //Transfer
        pickup_date: reservation.pickup_date,
        supplier: getSupplierName(reservationClass, reservation.supplier),
        totalCharge: getTotalCharge(reservationClass, reservation.pricing),
        netPrice: getNetprice(reservationClass, reservation.pricing),
        quotedPrice: getQuotedPrice(reservationClass, reservation.pricing),
        grossPrice: getGrossPrice(reservationClass, reservation.pricing),
        netProfit: getNetProfit(reservationClass, reservation.pricing),
        totalExtras: getTotalExtras(reservationClass, reservation.pricing),
        totalDiscounts: getTotalDiscounts(reservationClass, reservation.pricing),
        currency: getCurrency(
          reservationClass,
          reservation.service,
          reservation.pricing
        ),
        driver: getDriverName(reservationClass, reservation.driver),
        to: reservationClass === "Transfers" ? reservation.to : "",
        from: reservationClass === "Transfers" ? reservation.from : "",
        typeTransfer: reservationClass === "Transfers" ? reservation.type : "",
        passengersCount:
          reservationClass === "Transfers" ? reservation.passengersCount : "",
        flightNumber:
          reservationClass === "Transfers" ? reservation.flight_number : "",
        commission: reservation.commissions
          ? reservation.commissions.platform.amount
          : 0,
        class: reservationClass,
        referredBy: reservation.referredBy,
        stripeFee: reservation.commissions.payment_processor.amount || 0,
        reservation: reservation,
        stage: reservation.stage
      };
    });
  } catch (error) {
    return [];
  }
}
